import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { XMarkIcon, CheckCircleIcon, MagnifyingGlassIcon, PlusCircleIcon, ArrowPathIcon, CheckIcon, DocumentArrowDownIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon } from '@heroicons/react/24/solid';
import { Document, Page, pdfjs } from 'react-pdf';
import api from '../utils/api';
import { downloadReport } from '../utils/downloadReport';
import { qrMatchedNotice } from './qrPresentation';

// Configurar el worker de react-pdf desde un CDN con una versión específica compatible
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

// Debounce utility para la búsqueda
const debounce = (func, delay) => {
  let timeoutId;
  const wrapped = (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
  wrapped.cancel = () => clearTimeout(timeoutId);
  return wrapped;
};

// Normalizar texto para búsqueda (eliminando acentos y convirtiendo a mayúsculas)
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
};

const getMatrizadorKey = (value) => normalizeText(value || 'SIN MATRIZADOR');

const parseMoneyToCents = (value) => {
  const match = String(value ?? '').trim().match(/^([+-]?)(\d+)(?:\.(\d{1,2}))?$/);
  if (!match) return null;

  const sign = match[1] === '-' ? -1n : 1n;
  const fraction = (match[3] || '').padEnd(2, '0');
  return sign * ((window.BigInt(match[2]) * 100n) + window.BigInt(fraction || '0'));
};

const formatMoneyCents = (cents) => {
  const sign = cents < 0n ? '-' : '';
  const absolute = cents < 0n ? -cents : cents;
  return `${sign}${absolute / 100n}.${String(absolute % 100n).padStart(2, '0')}`;
};

const getTramiteAmountCents = (tramite) => {
  const precomputed = parseMoneyToCents(tramite?.monto_justificar);
  if (precomputed !== null && precomputed > 0n) return precomputed;
  const adjusted = parseMoneyToCents(tramite?.total_ajustado);
  const total = parseMoneyToCents(tramite?.total);
  if (adjusted !== null && adjusted > 0n) return adjusted;
  if (total !== null && total > 0n) return total;
  return adjusted ?? total ?? 0n;
};

const currentYear = new Date().getFullYear();
const monthOptions = [
  ['1', 'Enero'],
  ['2', 'Febrero'],
  ['3', 'Marzo'],
  ['4', 'Abril'],
  ['5', 'Mayo'],
  ['6', 'Junio'],
  ['7', 'Julio'],
  ['8', 'Agosto'],
  ['9', 'Septiembre'],
  ['10', 'Octubre'],
  ['11', 'Noviembre'],
  ['12', 'Diciembre'],
];

let nextReceiptClientId = 1;
const MAX_RECEIPTS = 5;
const PREFLIGHT_TIMEOUT_MS = 15000;
const FINAL_SUBMIT_TIMEOUT_MS = 30000;

const createEmptyComprobante = () => ({
  clientId: nextReceiptClientId++,
  bank_kind: '',
  bank_name: '',
  tipo_pago: '',
  numero_transaccion: '',
  fecha_deposito: '',
  monto_total: '',
  archivo: null,
  previewUrl: null,
  showPreview: false,
  zoomLevel: 1,
  numPages: null,
  fileError: null,
  preflightStatus: 'idle',
  preflightError: '',
  preflightResult: null,
  preflightCode: '',
  qr_manual_review: false,
  qr_override_reason: '',
  manual_account_owner: '',
});

const clearPreflightEvidence = (comprobante) => ({
  ...comprobante,
  numero_transaccion: '',
  fecha_deposito: '',
  monto_total: '',
  preflightStatus: 'idle',
  preflightError: '',
  preflightResult: null,
  preflightCode: '',
  qr_manual_review: false,
  qr_override_reason: '',
  manual_account_owner: '',
});

const isManualQrReview = (receipt) => (
  receipt.bank_kind === 'pichincha'
  && receipt.qr_manual_review
  && receipt.preflightCode === 'qr_unreadable'
  && receipt.preflightStatus === 'error'
);

const JustifyDeposits = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [days, setDays] = useState([]);
  const [selectedDay, setSelectedDay] = useState('');
  const [tramites, setTramites] = useState([]);
  const [matrizadores, setMatrizadores] = useState([]);
  const [selectedMatrizador, setSelectedMatrizador] = useState('');
  const [matrizadorSearch, setMatrizadorSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTramites, setSelectedTramites] = useState([]);
  const [comprobantes, setComprobantes] = useState([createEmptyComprobante()]);
  const [isLoadingFetch, setIsLoadingFetch] = useState(false);
  const [isLoadingSubmit, setIsLoadingSubmit] = useState(false);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const modalRef = useRef(null);
  const [justifiedTramites, setJustifiedTramites] = useState([]);
  const [tramitesToJustify, setTramitesToJustify] = useState([]);
  const [rejectedComprobantes, setRejectedComprobantes] = useState([]);
  const [showRejectedPopup, setShowRejectedPopup] = useState(false); // Nuevo estado para el popup
  const [totalTramites, setTotalTramites] = useState(0);
  const [creditCompanies, setCreditCompanies] = useState([]);
  const [creditResponsibles, setCreditResponsibles] = useState([]);
  const [creditAssignments, setCreditAssignments] = useState({});
  const [reportPeriod, setReportPeriod] = useState('day');
  const [reportYear, setReportYear] = useState(String(currentYear));
  const [reportMonth, setReportMonth] = useState(String(new Date().getMonth() + 1));
  const [submissionAlert, setSubmissionAlert] = useState(null);
  const submitButtonRef = useRef(null);
  const preflightRequestsRef = useRef(new Map());

  const cancelPreflightRequest = useCallback((clientId) => {
    const activeRequest = preflightRequestsRef.current.get(clientId);
    if (activeRequest) activeRequest.controller.abort();
    preflightRequestsRef.current.delete(clientId);
  }, []);

  // Validación de comprobantes
  const validateComprobantes = useCallback((comprobantes) => {
    const errors = [];
    comprobantes.forEach((c, index) => {
      const compErrors = {};
      if (!c.bank_kind) compErrors.bank_kind = 'Selecciona el banco.';
      if (c.bank_kind === 'other' && !c.bank_name.trim()) compErrors.bank_name = 'El nombre del banco es requerido.';
      if (!c.tipo_pago) compErrors.tipo_pago = 'El tipo de pago es requerido.';
      if (!c.numero_transaccion) compErrors.numero_transaccion = 'El número de transacción es requerido.';
      if (!c.fecha_deposito) compErrors.fecha_deposito = 'La fecha de depósito es requerida.';
      const amountCents = parseMoneyToCents(c.monto_total);
      if (amountCents === null || amountCents <= 0n) compErrors.monto_total = 'El monto debe ser mayor que 0.';
      if (!c.archivo) compErrors.archivo = 'El archivo es requerido.';
      if (c.bank_kind === 'pichincha' && c.preflightStatus !== 'verified' && !isManualQrReview(c)) {
        compErrors.preflight = 'El comprobante de Banco Pichincha debe superar la verificación QR.';
      }
      if (isManualQrReview(c)) {
        if (!c.manual_account_owner.trim()) compErrors.manual_account_owner = 'El titular declarado es requerido.';
        if (!c.qr_override_reason.trim()) compErrors.qr_override_reason = 'El motivo de revisión manual es requerido.';
      }
      if (Object.keys(compErrors).length > 0) errors[index] = compErrors;
    });
    return errors;
  }, []);

  // Filtrado adicional en frontend por matrizador y búsqueda
  const filteredTramitesMemo = useMemo(() => {
    let filtered = tramites;
    // Filtro por matrizador
    if (selectedMatrizador) {
      filtered = filtered.filter((t) => t.matrizador_key === selectedMatrizador);
    }
    // Filtro por búsqueda de matrizador
    filtered = filtered.filter((t) => normalizeText(t.usuario_matrizador).includes(normalizeText(matrizadorSearch)));
    // Filtro por búsqueda avanzada (código o descripción)
    if (searchQuery) {
      filtered = filtered.filter(
        (tramite) =>
          normalizeText(tramite.no_temporal).includes(normalizeText(searchQuery)) ||
          normalizeText(tramite.no_comprobante).includes(normalizeText(searchQuery)) ||
          normalizeText(tramite.acto).includes(normalizeText(searchQuery)) ||
          (tramite.items || []).some((item) => normalizeText(item.descripcion).includes(normalizeText(searchQuery)))
      );
    }
    return filtered;
  }, [tramites, selectedMatrizador, matrizadorSearch, searchQuery]);

  const tramiteById = useMemo(() => {
    const map = new Map();
    tramites.forEach((tramite) => {
      if (!tramite?.id) return;
      map.set(tramite.id, tramite);
    });
    return map;
  }, [tramites]);

  const responsiblesByCompany = useMemo(() => {
    const grouped = {};
    creditResponsibles.forEach((responsible) => {
      const companyId = String(responsible.company || '');
      if (!companyId) return;
      if (!grouped[companyId]) grouped[companyId] = [];
      grouped[companyId].push(responsible);
    });
    return grouped;
  }, [creditResponsibles]);

  // Cálculos optimizados
  const calculateTotalIngresos = useMemo(() => {
    const total = selectedTramites
      .reduce((total, id) => {
        const tramite = tramiteById.get(id);
        if (tramite && !justifiedTramites.includes(id)) {
          return total + getTramiteAmountCents(tramite);
        }
        return total;
      }, 0n);
    return formatMoneyCents(total);
  }, [selectedTramites, tramiteById, justifiedTramites]);

  const calculateTotalComprobantesCents = useMemo(() => {
    return comprobantes
      .filter((c) => c.tipo_pago && c.numero_transaccion && c.fecha_deposito && c.monto_total)
      .reduce((total, c) => total + (parseMoneyToCents(c.monto_total) ?? 0n), 0n);
  }, [comprobantes]);

  const calculateTotalComprobantes = formatMoneyCents(calculateTotalComprobantesCents);

  const calculateCurrentJustificationIngresos = useMemo(() => {
    const uniqueTramitesToJustify = [...new Set(tramitesToJustify)]; // Eliminar duplicados
    const total = uniqueTramitesToJustify
      .reduce((total, id) => {
        const tramite = tramiteById.get(id);
        if (tramite) {
          return total + getTramiteAmountCents(tramite);
        }
        return total;
      }, 0n);
    return formatMoneyCents(total);
  }, [tramitesToJustify, tramiteById]);

  const selectedVsPendingMetrics = useMemo(() => {
    const pendingTramites = filteredTramitesMemo.filter((t) => !justifiedTramites.includes(t.id));
    const pendingAmount = pendingTramites.reduce((acc, t) => acc + getTramiteAmountCents(t), 0n);
    const pendingIds = new Set(pendingTramites.map((t) => t.id));
    const selectedPendingIds = selectedTramites.filter((id) => pendingIds.has(id));
    const selectedPendingAmount = selectedPendingIds.reduce((acc, id) => {
      const tramite = tramiteById.get(id);
      return acc + getTramiteAmountCents(tramite);
    }, 0n);

    const missingCount = Math.max(pendingTramites.length - selectedPendingIds.length, 0);
    const difference = pendingAmount - selectedPendingAmount;
    const missingAmount = difference > 0n ? difference : 0n;

    return {
      pendingCount: pendingTramites.length,
      pendingAmount: formatMoneyCents(pendingAmount),
      selectedCount: selectedPendingIds.length,
      selectedAmount: formatMoneyCents(selectedPendingAmount),
      missingCount,
      missingAmount: formatMoneyCents(missingAmount),
    };
  }, [filteredTramitesMemo, justifiedTramites, selectedTramites, tramiteById]);

  const splitMetricsForIds = useCallback(
    (ids) => {
      const metrics = {
        cashCount: 0,
        cashAmount: 0n,
        creditCount: 0,
        creditAmount: 0n,
        totalCount: 0,
        totalAmount: 0n,
      };
      (Array.isArray(ids) ? ids : []).forEach((id) => {
        const tramite = tramiteById.get(id);
        if (!tramite) return;
        const amount = getTramiteAmountCents(tramite);
        metrics.totalCount += 1;
        metrics.totalAmount += amount;
        if (creditAssignments[id]?.isCredit) {
          metrics.creditCount += 1;
          metrics.creditAmount += amount;
        } else {
          metrics.cashCount += 1;
          metrics.cashAmount += amount;
        }
      });
      return metrics;
    },
    [creditAssignments, tramiteById]
  );

  const selectedSplitMetrics = useMemo(
    () => splitMetricsForIds(selectedTramites.filter((id) => !justifiedTramites.includes(id))),
    [selectedTramites, justifiedTramites, splitMetricsForIds]
  );

  const currentSplitMetrics = useMemo(
    () => splitMetricsForIds(tramitesToJustify),
    [tramitesToJustify, splitMetricsForIds]
  );

  const receiptErrors = useMemo(
    () => validateComprobantes(comprobantes),
    [comprobantes, validateComprobantes]
  );
  const receiptBlockingMessages = useMemo(() => {
    if (currentSplitMetrics.cashCount === 0) return [];
    const messages = [];
    receiptErrors.forEach((errors, index) => {
      Object.values(errors || {}).forEach((message) => {
        messages.push(`Comprobante ${index + 1}: ${message}`);
      });
    });
    if (currentSplitMetrics.cashAmount !== calculateTotalComprobantesCents) {
      messages.push(
        `El total de comprobantes debe ser $${formatMoneyCents(currentSplitMetrics.cashAmount)}.`
      );
    }
    return [...new Set(messages)];
  }, [currentSplitMetrics, calculateTotalComprobantesCents, receiptErrors]);
  const receiptReviewMessages = useMemo(() => (
    currentSplitMetrics.cashCount === 0
      ? []
      : comprobantes.flatMap((receipt, index) => (
          receipt.bank_kind === 'other'
            ? [`Comprobante ${index + 1}: requiere revisión manual del notario.`]
            : []
        ))
  ), [currentSplitMetrics.cashCount, comprobantes]);
  const cashReceiptsReady = currentSplitMetrics.cashCount === 0 || (
    comprobantes.length > 0
    && !receiptErrors.some(Boolean)
    && currentSplitMetrics.cashAmount === calculateTotalComprobantesCents
  );
  const isSubmitDisabled = isLoadingSubmit || !cashReceiptsReady;

  useEffect(() => {
    if (currentSplitMetrics.cashCount !== 0) return;
    setComprobantes((prev) => {
      const hasReceiptState = prev.length !== 1 || prev.some((receipt) => (
        receipt.bank_kind
        || receipt.bank_name
        || receipt.tipo_pago
        || receipt.numero_transaccion
        || receipt.fecha_deposito
        || receipt.monto_total
        || receipt.archivo
      ));
      if (!hasReceiptState) return prev;
      prev.forEach((receipt) => {
        cancelPreflightRequest(receipt.clientId);
        if (receipt.previewUrl) URL.revokeObjectURL(receipt.previewUrl);
      });
      return [createEmptyComprobante()];
    });
  }, [currentSplitMetrics.cashCount, cancelPreflightRequest]);

  const justificationProgress = useMemo(() => {
    if (selectedTramites.length === 0) return 0;
    return (justifiedTramites.length / selectedTramites.length) * 100;
  }, [selectedTramites, justifiedTramites]);

  // Cálculos de resúmenes (todos los trámites son ingresos justificables)
  const summaryTotalIngresos = useMemo(() => {
    return formatMoneyCents(
      tramites.reduce((total, t) => total + getTramiteAmountCents(t), 0n)
    );
  }, [tramites]);

  const matrizadorSummaryAdjusted = useMemo(() => {
    const summary = {};
    tramites.forEach((tramite) => {
      const matrizadorKey = tramite.matrizador_key || getMatrizadorKey(tramite.usuario_matrizador);
      const matrizadorLabel = tramite.usuario_matrizador || 'Sin Matrizador';
      if (!summary[matrizadorKey]) {
        summary[matrizadorKey] = {
          usuario_matrizador: matrizadorLabel,
          matrizador_key: matrizadorKey,
          tramites_count: 0,
          total_factura: 0n,
        };
      }
      summary[matrizadorKey].tramites_count += 1;
      summary[matrizadorKey].total_factura += getTramiteAmountCents(tramite);
    });
    return Object.values(summary).map((s) => ({
      ...s,
      total_factura: formatMoneyCents(s.total_factura),
    }));
  }, [tramites]);

  // Validar permisos
  useEffect(() => {
    if (!loading && (!user || !user.is_contador)) {
      toast.error('No tienes permisos para acceder a esta página.', { position: 'top-right', autoClose: 3000 });
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Cargar días disponibles
  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response = await api.get('/api/audit/document-days/', {
          params: { page: 1, page_size: 1000 }
        });
        const fetchedDays = Array.isArray(response.data.days) ? response.data.days : [];
        setDays(fetchedDays);
        if (fetchedDays.length > 0) {
          setSelectedDay(fetchedDays[0]);
        }
      } catch (error) {
        toast.error('Error al cargar los días disponibles.', { position: 'top-right', autoClose: 5000 });
        setError('Error al cargar los días disponibles.');
      }
    };
    if (!loading && user && user.is_contador) {
      fetchDays();
    }
  }, [user, loading]);

  useEffect(() => {
    const fetchCreditCatalogs = async () => {
      try {
        const [companiesResponse, responsiblesResponse] = await Promise.all([
          api.get('/api/audit/credit-companies/'),
          api.get('/api/audit/credit-responsibles/'),
        ]);
        setCreditCompanies(Array.isArray(companiesResponse.data?.results) ? companiesResponse.data.results : []);
        setCreditResponsibles(Array.isArray(responsiblesResponse.data?.results) ? responsiblesResponse.data.results : []);
      } catch (err) {
        toast.error('No se pudo cargar empresas/responsables de crédito.', { position: 'top-right' });
        setCreditCompanies([]);
        setCreditResponsibles([]);
      }
    };
    if (!loading && user && user.is_contador) {
      fetchCreditCatalogs();
    }
  }, [user, loading]);

  // Cargar trámites y comprobantes en paralelo
  const fetchTramitesAndComprobantes = useCallback(async () => {
    if (!selectedDay) {
      toast.error('Por favor, selecciona un día.', { position: 'top-right', autoClose: 3000 });
      return;
    }
    setIsLoadingFetch(true);
    setError(null);
    try {
      const startTime = performance.now();
      const [tramitesResponse, comprobantesResponse] = await Promise.all([
        api.get('/api/audit/list/', {
          params: { fecha_documento: selectedDay, justificable: true, page: 1, page_size: 1000 },
        }),
        api.get('/api/audit/comprobantes/', {
          params: { fecha_documento: selectedDay },
        }),
      ]);

      const rawTramites = Array.isArray(tramitesResponse.data?.tramites) ? tramitesResponse.data.tramites : [];
      const byId = new Map();
      rawTramites.forEach((tramite) => {
        if (!tramite?.id || byId.has(tramite.id)) return;
        const usuarioMatrizador = (tramite.usuario_matrizador || 'Sin Matrizador').trim() || 'Sin Matrizador';
        const matrizadorKey = getMatrizadorKey(tramite.matrizador_key || usuarioMatrizador);
        const total = parseFloat(tramite.total || 0) || 0;
        const totalAjustado = parseFloat(tramite.total_ajustado || 0) || 0;
        byId.set(tramite.id, {
          ...tramite,
          usuario_matrizador: usuarioMatrizador,
          matrizador_key: matrizadorKey,
          total,
          total_ajustado: totalAjustado,
          monto_justificar: formatMoneyCents(getTramiteAmountCents({ total, total_ajustado: totalAjustado })),
          comprobante_afectado: tramite.comprobante_afectado || '',
        });
      });
      const tramitesProcesados = Array.from(byId.values());
      setTramites(tramitesProcesados);
      setTotalTramites(tramitesProcesados.length);
      setCreditAssignments({});

      const uniqueMatrizadores = Array.from(
        tramitesProcesados.reduce((acc, t) => {
          if (!acc.has(t.matrizador_key)) acc.set(t.matrizador_key, t.usuario_matrizador);
          return acc;
        }, new Map()).entries()
      )
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => normalizeText(a.label).localeCompare(normalizeText(b.label)));
      setMatrizadores(uniqueMatrizadores);

      const rejected = comprobantesResponse.data.filter((comprobante) => comprobante.aprobado === false && comprobante.notes);
      setRejectedComprobantes(rejected);
      if (rejected.length > 0) {
        setShowRejectedPopup(true);
        // Auto-cerrar el popup después de 10 segundos
        setTimeout(() => {
          setShowRejectedPopup(false);
        }, 10000);
      }

      const endTime = performance.now();
      console.log(`Tiempo de carga de trámites y comprobantes: ${(endTime - startTime) / 1000} segundos`);
    } catch (err) {
      if (err.response) {
        setError(`Error del servidor al cargar los trámites: ${err.response.data.error || 'Error desconocido'}.`);
      } else if (err.request) {
        setError('No se pudo conectar al servidor. Verifica tu conexión a internet.');
      } else {
        setError(`Error al cargar los trámites: ${err.message}`);
      }
      toast.error('Error al cargar los trámites o comprobantes.', { position: 'top-right', autoClose: 5000 });
    } finally {
      setIsLoadingFetch(false);
    }
  }, [selectedDay]);

  useEffect(() => {
    if (showModal && modalRef.current) {
      const firstInput = modalRef.current.querySelector('input, select, textarea');
      if (firstInput) firstInput.focus();
    }
  }, [showModal]);

  const handleDayChange = useCallback((e) => {
    setSelectedDay(e.target.value);
    setSelectedMatrizador('');
    setTramites([]);
    setMatrizadores([]);
    setSelectedTramites([]);
    setCreditAssignments({});
    setRejectedComprobantes([]);
    setShowRejectedPopup(false); // Cerrar el popup al cambiar de día
    setError(null);
    setMatrizadorSearch('');
    setSearchQuery('');
  }, []);

  const handleMatrizadorChange = useCallback((e) => {
    setSelectedMatrizador(e.target.value);
    setSelectedTramites([]);
  }, []);

  const handleTramiteSelection = useCallback((tramiteId) => {
    setSelectedTramites((prev) => {
      if (prev.includes(tramiteId)) {
        setCreditAssignments((assignments) => {
          const next = { ...assignments };
          delete next[tramiteId];
          return next;
        });
        return prev.filter((id) => id !== tramiteId);
      }
      return [...prev, tramiteId];
    });
  }, []);

  const handleCreditToggle = useCallback((tramiteId) => {
    setCreditAssignments((prev) => {
      const next = { ...prev };
      if (next[tramiteId]?.isCredit) {
        delete next[tramiteId];
      } else {
        next[tramiteId] = { isCredit: true, company_id: '', responsible_id: '' };
      }
      return next;
    });
    setSelectedTramites((prev) => (prev.includes(tramiteId) ? prev : [...prev, tramiteId]));
  }, []);

  const handleCreditAssignmentChange = useCallback((tramiteId, field, value) => {
    setCreditAssignments((prev) => {
      const current = prev[tramiteId] || { isCredit: true, company_id: '', responsible_id: '' };
      return {
        ...prev,
        [tramiteId]: {
          ...current,
          isCredit: true,
          [field]: value,
          ...(field === 'company_id' ? { responsible_id: '' } : {}),
        },
      };
    });
    setSelectedTramites((prev) => (prev.includes(tramiteId) ? prev : [...prev, tramiteId]));
  }, []);

  const handleOpenModal = useCallback(() => {
    if (selectedTramites.length === 0) {
      toast.error('Por favor, selecciona al menos un trámite.', { position: 'top-right' });
      return;
    }
    const missingCreditData = selectedTramites.filter((id) => {
      const assignment = creditAssignments[id];
      return assignment?.isCredit && (!assignment.company_id || !assignment.responsible_id);
    });
    if (missingCreditData.length > 0) {
      toast.error('Completa empresa y responsable para cada trámite marcado a crédito.', { position: 'top-right' });
      return;
    }
    setTramitesToJustify(selectedTramites.filter((id) => !justifiedTramites.includes(id)));
    setComprobantes([createEmptyComprobante()]);
    setShowModal(true);
  }, [selectedTramites, justifiedTramites, creditAssignments]);

  const handleComprobanteChange = useCallback((index, field, value) => {
    setComprobantes((prev) => prev.map((comp, i) => {
      if (i !== index) return comp;
      if (field === 'qr_manual_review') {
        if (comp.preflightCode !== 'qr_unreadable' || comp.preflightStatus !== 'error') return comp;
        cancelPreflightRequest(comp.clientId);
        return {
          ...clearPreflightEvidence(comp),
          preflightStatus: comp.preflightStatus,
          preflightCode: comp.preflightCode,
          preflightError: comp.preflightError,
          qr_manual_review: value,
        };
      }
      if (field === 'bank_kind') {
        cancelPreflightRequest(comp.clientId);
        return {
          ...clearPreflightEvidence(comp),
          bank_kind: value,
          bank_name: '',
        };
      }
      if (field === 'tipo_pago' && comp.bank_kind === 'pichincha') {
        cancelPreflightRequest(comp.clientId);
        return { ...clearPreflightEvidence(comp), tipo_pago: value };
      }
      return { ...comp, [field]: value };
    }));
  }, [cancelPreflightRequest]);

  const handleFileChange = useCallback((index, e) => {
    const file = e.target.files[0];
    if (file && !['application/pdf', 'image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      toast.error('Por favor, selecciona un archivo PDF, PNG o JPG.', { position: 'top-right' });
      return;
    }
    const previewUrl = file ? URL.createObjectURL(file) : null;
    setComprobantes((prev) => prev.map((comp, i) => {
      if (i !== index) return comp;
      if (comp.previewUrl) URL.revokeObjectURL(comp.previewUrl);
      cancelPreflightRequest(comp.clientId);
      const next = {
        ...comp,
        archivo: file,
        previewUrl,
        showPreview: false,
        zoomLevel: 1,
        numPages: null,
        fileError: null,
      };
      return comp.bank_kind === 'pichincha' ? clearPreflightEvidence(next) : next;
    }));
  }, [cancelPreflightRequest]);

  const handleRemoveFile = useCallback((index) => {
    setComprobantes((prev) =>
      prev.map((comp, i) => {
        if (i === index && comp.previewUrl) {
          URL.revokeObjectURL(comp.previewUrl);
        }
        if (i === index) cancelPreflightRequest(comp.clientId);
        return i === index
          ? clearPreflightEvidence({ ...comp, archivo: null, previewUrl: null, showPreview: false, zoomLevel: 1, numPages: null, fileError: null })
          : comp;
      })
    );
  }, [cancelPreflightRequest]);

  const addComprobante = useCallback(() => {
    setComprobantes((prev) => (
      prev.length < MAX_RECEIPTS ? [...prev, createEmptyComprobante()] : prev
    ));
  }, []);

  const removeComprobante = useCallback((index) => {
    setComprobantes((prev) => {
      if (prev.length <= 1) return prev;
      const removed = prev[index];
      cancelPreflightRequest(removed.clientId);
      if (removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, receiptIndex) => receiptIndex !== index);
    });
  }, [cancelPreflightRequest]);

  const runPichinchaPreflight = useCallback(async (comprobante) => {
    cancelPreflightRequest(comprobante.clientId);
    const requestToken = Symbol(`receipt-${comprobante.clientId}`);
    const controller = new AbortController();
    const activeRequest = { requestToken, controller };
    preflightRequestsRef.current.set(comprobante.clientId, activeRequest);
    setComprobantes((prev) => prev.map((row) => (
      row.clientId === comprobante.clientId
        ? { ...row, preflightStatus: 'loading', preflightError: '' }
        : row
    )));

    const formData = new FormData();
    formData.append('bank_kind', 'pichincha');
    formData.append('tipo_pago', comprobante.tipo_pago);
    formData.append('archivo', comprobante.archivo);
    const timeoutId = window.setTimeout(() => controller.abort(), PREFLIGHT_TIMEOUT_MS);

    try {
      const response = await api.post('/api/audit/bank-receipts/preflight/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
        timeout: PREFLIGHT_TIMEOUT_MS,
      });
      if (preflightRequestsRef.current.get(comprobante.clientId) !== activeRequest) return;
      setComprobantes((prev) => prev.map((row) => (
        row.clientId === comprobante.clientId
          ? {
              ...row,
              numero_transaccion: response.data.numero_transaccion,
              fecha_deposito: response.data.fecha_deposito,
              monto_total: response.data.monto_total,
              preflightStatus: 'verified',
              preflightError: '',
              preflightResult: response.data,
            }
          : row
      )));
    } catch (preflightError) {
      if (preflightRequestsRef.current.get(comprobante.clientId) !== activeRequest) return;
      const responseStatus = Number(preflightError?.response?.status || 0);
      const isBusinessFailure = (
        responseStatus >= 400
        && responseStatus < 500
        && responseStatus !== 408
        && responseStatus !== 429
      );
      const message = String(
        preflightError?.response?.data?.error
        || 'No se pudo completar la verificación. Reintenta.'
      );
      setComprobantes((prev) => prev.map((row) => (
        row.clientId === comprobante.clientId
          ? {
              ...clearPreflightEvidence(row),
              preflightStatus: isBusinessFailure ? 'error' : 'transient-error',
              preflightError: message,
              preflightCode: String(preflightError?.response?.data?.code || ''),
            }
          : row
      )));
    } finally {
      window.clearTimeout(timeoutId);
      if (preflightRequestsRef.current.get(comprobante.clientId) === activeRequest) {
        preflightRequestsRef.current.delete(comprobante.clientId);
      }
    }
  }, [cancelPreflightRequest]);

  const retryPreflight = useCallback((clientId) => {
    setComprobantes((prev) => prev.map((receipt) => (
      receipt.clientId === clientId
        ? { ...receipt, preflightStatus: 'idle', preflightError: '' }
        : receipt
    )));
  }, []);

  useEffect(() => {
    comprobantes.forEach((comprobante) => {
      if (
        comprobante.bank_kind === 'pichincha'
        && comprobante.tipo_pago
        && comprobante.archivo
        && comprobante.preflightStatus === 'idle'
      ) {
        runPichinchaPreflight(comprobante);
      }
    });
  }, [comprobantes, runPichinchaPreflight]);

  useEffect(() => () => {
    preflightRequestsRef.current.forEach(({ controller }) => controller.abort());
    preflightRequestsRef.current.clear();
  }, []);

  const handleJustifySelection = (tramiteId) => {
    setTramitesToJustify((prev) =>
      prev.includes(tramiteId) ? prev.filter((id) => id !== tramiteId) : [...prev, tramiteId]
    );
  };

  const submitJustification = useCallback(
    async () => {
      setSubmissionAlert(null);
      if (tramitesToJustify.length === 0) {
        toast.error('Por favor, selecciona al menos un trámite para justificar.', { position: 'top-right' });
        return;
      }
      const creditItems = tramitesToJustify
        .filter((id) => creditAssignments[id]?.isCredit)
        .map((id) => ({
          tramite_id: id,
          company_id: creditAssignments[id]?.company_id,
          responsible_id: creditAssignments[id]?.responsible_id,
        }));
      const cashTramiteIds = tramitesToJustify.filter((id) => !creditAssignments[id]?.isCredit);
      const hasCreditItems = creditItems.length > 0;
      const missingCreditData = creditItems.filter((item) => !item.company_id || !item.responsible_id);
      if (missingCreditData.length > 0) {
        toast.error('Completa empresa y responsable para cada trámite a crédito.', { position: 'top-right' });
        return;
      }

      const validComprobantes = comprobantes.filter(
        (c) => c.bank_kind
          && (c.bank_kind !== 'other' || c.bank_name.trim())
          && c.tipo_pago
          && c.numero_transaccion
          && c.fecha_deposito
          && c.monto_total
          && c.archivo
          && (c.bank_kind !== 'pichincha' || c.preflightStatus === 'verified' || isManualQrReview(c))
      );
      if (cashTramiteIds.length > 0) {
        const errors = validateComprobantes(comprobantes);
        if (validComprobantes.length === 0 || errors.length > 0) {
          toast.error('Completa todos los campos de al menos un comprobante para el efectivo.', { position: 'top-right' });
          return;
        }
      }

      const totalIngresosToJustify = currentSplitMetrics.cashAmount;
      if (cashTramiteIds.length > 0 && totalIngresosToJustify !== calculateTotalComprobantesCents) {
        toast.error(
          `El total efectivo ($${formatMoneyCents(totalIngresosToJustify)}) debe coincidir exactamente con el total de los comprobantes ($${calculateTotalComprobantes}).`,
          { position: 'top-right' }
        );
        return;
      }

      const formData = new FormData();
      const submissionComprobantes = cashTramiteIds.length > 0 ? validComprobantes : [];
      submissionComprobantes.forEach((c, idx) => {
        formData.append(`archivos[${idx}]`, c.archivo);
      });
      const comprobantesPayload = submissionComprobantes.map((c) => ({
        bank_kind: c.bank_kind,
        bank_name: c.bank_kind === 'other' ? c.bank_name.trim() : '',
        tipo_pago: c.tipo_pago,
        numero_transaccion: c.numero_transaccion,
        fecha_deposito: c.fecha_deposito,
        monto_total: c.monto_total,
        ...(isManualQrReview(c) ? {
          qr_manual_review: true,
          qr_override_reason: c.qr_override_reason.trim(),
          manual_account_owner: c.manual_account_owner.trim(),
        } : {}),
      }));
      formData.append('comprobantes', JSON.stringify(comprobantesPayload));
      if (hasCreditItems) {
        cashTramiteIds.forEach((id) => formData.append('cash_tramites_ids', id));
        formData.append('credit_items', JSON.stringify(creditItems));
      } else {
        tramitesToJustify.forEach((id) => {
          formData.append('tramites_ids', id);
        });
      }

      setIsLoadingSubmit(true);
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), FINAL_SUBMIT_TIMEOUT_MS);
      try {
        await api.post(hasCreditItems ? '/api/audit/justify-deposit-split/' : '/api/audit/justify-deposit/', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          signal: controller.signal,
          timeout: FINAL_SUBMIT_TIMEOUT_MS,
        });
        const processedIds = new Set(tramitesToJustify);
        const updatedTramites = tramites.filter((t) => !processedIds.has(t.id));
        setTramites(updatedTramites);
        setTotalTramites(updatedTramites.length);
        setCreditAssignments((prev) => {
          const next = { ...prev };
          tramitesToJustify.forEach((id) => delete next[id]);
          return next;
        });
        toast.success(
          submissionComprobantes.some((receipt) => receipt.bank_kind === 'other')
            ? 'Enviado para revisión manual con alerta.'
            : `${qrMatchedNotice} Pendiente de aprobación notarial.`,
          { position: 'top-right' }
        );
        setJustifiedTramites((prev) => [...prev, ...tramitesToJustify]);
        setTramitesToJustify(selectedTramites.filter((id) => !justifiedTramites.includes(id) && !tramitesToJustify.includes(id)));
        setComprobantes([createEmptyComprobante()]);
        if (selectedTramites.every((id) => justifiedTramites.includes(id) || tramitesToJustify.includes(id))) {
          setSelectedTramites([]);
          setJustifiedTramites([]);
          setShowModal(false);
        }
      } catch (error) {
        const responseData = error?.response?.data;
        const status = Number(error?.response?.status || 0);
        const message = status >= 500 || !responseData
          ? 'No se pudo confirmar el resultado. Verifica la lista o el historial antes de volver a intentar; no reenvíes la justificación sin reconciliar su estado.'
          : String(responseData.error || 'No se pudo enviar la justificación. Inténtalo de nuevo.');
        setSubmissionAlert(message);
        toast.error(message, { position: 'top-right' });
      } finally {
        window.clearTimeout(timeoutId);
        setIsLoadingSubmit(false);
      }
    },
    [tramitesToJustify, selectedTramites, justifiedTramites, comprobantes, calculateTotalComprobantes, calculateTotalComprobantesCents, validateComprobantes, tramites, creditAssignments, currentSplitMetrics]
  );

  const handleSubmit = useCallback((event) => {
    event.preventDefault();
    submitJustification();
  }, [submitJustification]);
  const handleCloseModal = () => {
    setSelectedTramites([]);
    setJustifiedTramites([]);
    setCreditAssignments({});
    setShowModal(false);
    setComprobantes((prev) =>
      prev.map((comp) => {
        if (comp.previewUrl) {
          URL.revokeObjectURL(comp.previewUrl);
        }
        cancelPreflightRequest(comp.clientId);
        return createEmptyComprobante();
      })
    );
  };

  const togglePreview = (e, index) => {
    e.preventDefault();
    setComprobantes((prev) =>
      prev.map((comp, i) => (i === index ? { ...comp, showPreview: !comp.showPreview, fileError: null, numPages: null } : comp))
    );
  };

  const handleZoomIn = (index) => {
    setComprobantes((prev) =>
      prev.map((comp, i) => (i === index ? { ...comp, zoomLevel: Math.min(comp.zoomLevel + 0.2, 3) } : comp))
    );
  };

  const handleZoomOut = (index) => {
    setComprobantes((prev) =>
      prev.map((comp, i) => (i === index ? { ...comp, zoomLevel: Math.max(comp.zoomLevel - 0.2, 0.5) } : comp))
    );
  };

  const onDocumentLoadSuccess = (index, { numPages }) => {
    setComprobantes((prev) =>
      prev.map((comp, i) => (i === index ? { ...comp, numPages: numPages, fileError: null } : comp))
    );
  };

  const onDocumentLoadError = (index) => {
    setComprobantes((prev) =>
      prev.map((comp, i) => (i === index ? { ...comp, fileError: 'No se pudo cargar el PDF. Intenta descargarlo directamente.', numPages: null } : comp))
    );
    toast.error('Error al cargar el PDF.', { position: 'top-right' });
  };

  const onImageLoadError = (index) => {
    setComprobantes((prev) =>
      prev.map((comp, i) => (i === index ? { ...comp, fileError: 'No se pudo cargar la imagen. Intenta descargarla directamente.' } : comp))
    );
    toast.error('Error al cargar la imagen.', { position: 'top-right' });
  };

  // Debounce para la búsqueda de matrizadores
  const debouncedSetMatrizadorSearch = useMemo(
    () =>
      debounce((value) => {
        setMatrizadorSearch(value);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      if (debouncedSetMatrizadorSearch && debouncedSetMatrizadorSearch.cancel) {
        debouncedSetMatrizadorSearch.cancel();
      }
    };
  }, [debouncedSetMatrizadorSearch]);

  const handleMatrizadorSearchChange = (e) => {
    debouncedSetMatrizadorSearch(e.target.value);
  };

  // Debounce para la búsqueda avanzada
  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        setSearchQuery(value);
      }, 300),
    []
  );

  useEffect(() => {
    return () => {
      if (debouncedSetSearchQuery && debouncedSetSearchQuery.cancel) {
        debouncedSetSearchQuery.cancel();
      }
    };
  }, [debouncedSetSearchQuery]);

  const handleSearchQueryChange = (e) => {
    debouncedSetSearchQuery(e.target.value);
  };

  const handleDownloadReport = useCallback(
    async (format) => {
      const params = { period: reportPeriod };
      if (reportPeriod === 'day') {
        if (!selectedDay) {
          toast.error('Selecciona un día para generar el reporte.');
          return;
        }
        params.date = selectedDay;
      } else if (reportPeriod === 'month') {
        if (!reportYear || !reportMonth) {
          toast.error('Selecciona año y mes para generar el reporte.');
          return;
        }
        params.year = reportYear;
        params.month = reportMonth;
      } else if (reportPeriod === 'year') {
        if (!reportYear) {
          toast.error('Selecciona un año para generar el reporte.');
          return;
        }
        params.year = reportYear;
      }
      if (selectedMatrizador) {
        const selected = matrizadores.find((matrizador) => matrizador.key === selectedMatrizador);
        params.usuario_matrizador = selected?.label || selectedMatrizador;
      }
      await downloadReport({
        api,
        endpoint: '/api/audit/reports/justification-tramites/',
        params,
        format,
        baseName: 'reporte-tramites',
      });
    },
    [reportPeriod, selectedDay, reportYear, reportMonth, selectedMatrizador, matrizadores]
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex flex-wrap justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Justificar Depósitos</h1>
        </div>
        <Link
          to="/dashboard/contador"
          className="mt-2 sm:mt-0 bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all"
        >
          Volver al Dashboard
        </Link>
      </header>

      <main className="flex-1">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
        >
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Seleccionar Día</h2>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
            <div className="w-full sm:w-1/3">
              <label htmlFor="fecha_documento" className="block text-sm sm:text-lg font-semibold mb-2">
                Día de los Trámites
              </label>
              <select
                id="fecha_documento"
                value={selectedDay}
                onChange={handleDayChange}
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
              >
                <option value="">Seleccione un día</option>
                {days.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <motion.button
              onClick={fetchTramitesAndComprobantes}
              disabled={isLoadingFetch || !selectedDay}
              className={`bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg hover:bg-notaria-red hover:text-white transition-all ${
                isLoadingFetch || !selectedDay ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              whileHover={{ scale: isLoadingFetch || !selectedDay ? 1 : 1.02 }}
              whileTap={{ scale: isLoadingFetch || !selectedDay ? 1 : 0.98 }}
              aria-label="Cargar trámites"
            >
              {isLoadingFetch ? (
                <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
              ) : (
                'Cargar Trámites'
              )}
            </motion.button>
          </div>
          <div className="mt-5 border-t border-white/10 pt-4">
            <p className="mb-3 text-sm font-semibold text-gray-200">Reporte Excel de trámites</p>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
              <select
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                aria-label="Periodo del reporte"
              >
                <option value="day">Por día</option>
                <option value="month">Por mes</option>
                <option value="year">Por año</option>
              </select>
              <input
                type="number"
                value={reportYear}
                onChange={(e) => setReportYear(e.target.value)}
                className="rounded-lg bg-notaria-blue-2 p-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                aria-label="Año del reporte"
              />
              <select
                value={reportMonth}
                onChange={(e) => setReportMonth(e.target.value)}
                disabled={reportPeriod !== 'month'}
                className={`rounded-lg bg-notaria-blue-2 p-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow ${reportPeriod !== 'month' ? 'opacity-50' : ''}`}
                aria-label="Mes del reporte"
              >
                {monthOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => handleDownloadReport('xlsx')}
                className="rounded-lg bg-notaria-yellow px-4 py-2 font-bold text-notaria-blue-1 hover:bg-notaria-red hover:text-white"
              >
                Descargar XLSX
              </button>
              <button
                type="button"
                onClick={() => handleDownloadReport('xls')}
                className="rounded-lg bg-notaria-blue-2 px-4 py-2 font-bold text-white hover:bg-notaria-blue-3"
              >
                Descargar XLS
              </button>
            </div>
          </div>
        </motion.div>

        {isLoadingFetch ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-200"
          >
            Cargando trámites, por favor espera...
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-7xl mx-auto bg-red-500 text-white p-4 rounded-lg mb-6 sm:mb-8"
          >
            {error}
          </motion.div>
        ) : selectedDay && tramites.length > 0 ? (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold">Seleccionar Trámites a Justificar</h2>
              <div className="w-full sm:w-1/3 mt-2 sm:mt-0">
                <label htmlFor="usuario_matrizador" className="block text-sm sm:text-lg font-semibold mb-2">
                  Seleccionar Matrizador
                </label>
                <select
                  id="usuario_matrizador"
                  value={selectedMatrizador}
                  onChange={handleMatrizadorChange}
                  className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Seleccionar matrizador"
                >
                  <option value="">Todos los Matrizadores</option>
                  {matrizadores.map((matrizador) => (
                    <option key={matrizador.key} value={matrizador.key}>
                      {matrizador.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="mb-4 bg-notaria-blue-2 p-4 rounded-lg">
              <p className="text-lg font-semibold">Resumen General:</p>
              <p className="text-lg">Total Ingresos a Justificar: ${summaryTotalIngresos}</p>
              <p className="text-lg">Total Trámites a Justificar: {totalTramites}</p>
              <p className="text-sm text-gray-200">
                Selección actual: efectivo ${formatMoneyCents(selectedSplitMetrics.cashAmount)} / crédito ${formatMoneyCents(selectedSplitMetrics.creditAmount)}
              </p>
            </div>
            {selectedMatrizador && matrizadorSummaryAdjusted.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-4 bg-notaria-blue-2 p-4 rounded-lg"
              >
                {matrizadorSummaryAdjusted
                  .filter((summary) => summary.matrizador_key === selectedMatrizador)
                  .map((summary) => (
                    <div key={summary.matrizador_key}>
                      <p className="text-lg font-semibold">Resumen para {summary.usuario_matrizador}:</p>
                      <p className="text-lg">Trámites Pendientes: {summary.tramites_count}</p>
                      <p className="text-lg">Total Ingresos a Justificar: ${summary.total_factura}</p>
                    </div>
                  ))}
              </motion.div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="relative w-full">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar matrizador..."
                  onChange={handleMatrizadorSearchChange}
                  className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Buscar matrizador"
                />
              </div>
              <div className="relative w-full">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por código o descripción..."
                  onChange={handleSearchQueryChange}
                  className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Buscar por código o descripción"
                />
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-notaria-blue-2 text-white">
                    <th className="p-3 text-left w-16">Sel.</th>
                    <th className="p-3 text-left">Crédito</th>
                    <th className="p-3 text-left">No. Temporal</th>
                    <th className="p-3 text-left">No. Comprobante</th>
                    <th className="p-3 text-left">Fecha Comprobante</th>
                    <th className="p-3 text-left">Acto</th>
                    <th className="p-3 text-left">Total Ajustado</th>
                    <th className="p-3 text-left min-w-[260px]">Empresa / Responsable</th>
<th className="p-3 text-left">Semáforo</th>
                    <th className="p-3 text-left">Tipo</th>
                    <th className="p-3 text-left">Total</th>
                    <th className="p-3 text-left">Comprobante Afectado</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTramitesMemo.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="p-3 text-center text-gray-400">
                        No hay trámites que coincidan con los criterios de búsqueda.
                      </td>
                    </tr>
                  ) : (
                    filteredTramitesMemo.map((tramite) => (
                      <motion.tr
                        key={tramite.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="border-b border-notaria-blue-2 hover:bg-notaria-blue-3"
                      >
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={selectedTramites.includes(tramite.id)}
                            onChange={() => handleTramiteSelection(tramite.id)}
                            className="h-5 w-5 text-notaria-yellow rounded focus:ring-notaria-yellow"
                            aria-label={`Seleccionar trámite ${tramite.no_temporal}`}
                          />
                        </td>
                        <td className="p-3">
                          <input
                            type="checkbox"
                            checked={Boolean(creditAssignments[tramite.id]?.isCredit)}
                            onChange={() => handleCreditToggle(tramite.id)}
                            className="h-5 w-5 text-notaria-yellow rounded focus:ring-notaria-yellow"
                            aria-label={`Marcar trámite ${tramite.no_temporal} como crédito`}
                          />
                        </td>
                        <td className="p-3">{tramite.no_temporal || '-'}</td>
                        <td className="p-3">{tramite.no_comprobante || '-'}</td>
                        <td className="p-3">{tramite.fecha_comprobante || '-'}</td>
                        <td className="p-3">
                          <div className="max-h-[80px] overflow-y-auto text-sm">
                            {(tramite.items || []).map((item, index) => (
                              <div key={index} className="py-0.5">
                                {item.descripcion}
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="p-3">${formatMoneyCents(getTramiteAmountCents(tramite))}</td>
                        <td className="p-3">
                          {creditAssignments[tramite.id]?.isCredit ? (
                            <div className="space-y-2 min-w-[240px]">
                              <select
                                value={creditAssignments[tramite.id]?.company_id || ''}
                                onChange={(e) => handleCreditAssignmentChange(tramite.id, 'company_id', e.target.value)}
                                className="w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                aria-label={`Empresa para crédito del trámite ${tramite.no_temporal}`}
                              >
                                <option value="">Empresa</option>
                                {creditCompanies.map((company) => (
                                  <option key={company.id} value={company.id}>
                                    {company.nombre}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={creditAssignments[tramite.id]?.responsible_id || ''}
                                onChange={(e) => handleCreditAssignmentChange(tramite.id, 'responsible_id', e.target.value)}
                                className="w-full p-2 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                aria-label={`Responsable para crédito del trámite ${tramite.no_temporal}`}
                                disabled={!creditAssignments[tramite.id]?.company_id}
                              >
                                <option value="">Responsable</option>
                                {(responsiblesByCompany[String(creditAssignments[tramite.id]?.company_id || '')] || []).map((responsible) => (
                                  <option key={responsible.id} value={responsible.id}>
                                    {responsible.nombre}{responsible.departamento ? ` - ${responsible.departamento}` : ''}
                                  </option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <span className="text-gray-400">Efectivo</span>
                          )}
                        </td>
<td className="p-3">
                          <span
                            className={`inline-block w-4 h-4 rounded-full ${
                              tramite.semaforo === 'rojo'
                                ? 'bg-red-500'
                                : tramite.semaforo === 'amarillo'
                                ? 'bg-yellow-500'
                                : 'bg-green-500'
                            }`}
                            aria-label={`Estado del semáforo: ${tramite.semaforo}`}
                          ></span>
                        </td>
                        <td className="p-3">{tramite.tipo.charAt(0).toUpperCase() + tramite.tipo.slice(1)}</td>
                        <td className="p-3">${parseFloat(tramite.total || 0).toFixed(2)}</td>
                        <td className="p-3">{tramite.comprobante_afectado || '-'}</td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {selectedTramites.length > 0 && (
              <motion.button
                onClick={handleOpenModal}
                className="mt-4 w-full bg-notaria-yellow text-notaria-blue-1 font-bold py-2 rounded-lg hover:bg-notaria-red hover:text-white transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                aria-label="Justificar depósitos seleccionados"
              >
                Justificar Depósitos Seleccionados
              </motion.button>
            )}
            {selectedTramites.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mt-4 bg-notaria-blue-2 p-4 rounded-lg"
              >
                <p className="text-lg font-semibold">Trámites Seleccionados: {selectedVsPendingMetrics.selectedCount}</p>
                <p className="text-lg">Total seleccionado: ${formatMoneyCents(selectedSplitMetrics.totalAmount)}</p>
                <p className="text-lg">Efectivo a depositar: ${formatMoneyCents(selectedSplitMetrics.cashAmount)}</p>
                <p className="text-lg">Crédito por cobrar: ${formatMoneyCents(selectedSplitMetrics.creditAmount)}</p>
                <p className="text-lg">
                  Faltan por justificar: {selectedVsPendingMetrics.missingCount} trámites (${selectedVsPendingMetrics.missingAmount})
                </p>
              </motion.div>
            )}
          </motion.div>
        ) : selectedDay ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-400"
          >
            No hay trámites disponibles para justificar en el día seleccionado.
          </motion.div>
        ) : null}
      </main>

      {/* Popup de Comprobantes Rechazados */}
      <AnimatePresence>
        {showRejectedPopup && rejectedComprobantes.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.5 }}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 max-w-3xl w-full bg-notaria-blue-1 bg-opacity-95 p-6 rounded-2xl shadow-2xl z-50"
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl sm:text-2xl font-semibold text-notaria-red">Comprobantes Rechazados</h2>
              <motion.button
                onClick={() => setShowRejectedPopup(false)}
                className="text-gray-300 hover:text-notaria-yellow transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                aria-label="Cerrar popup de comprobantes rechazados"
              >
                <XMarkIcon className="w-6 h-6" />
              </motion.button>
            </div>
            <div className="space-y-4 max-h-[50vh] overflow-y-auto">
              {rejectedComprobantes.map((comprobante) => (
                <div key={comprobante.id} className="bg-notaria-red bg-opacity-20 p-4 rounded-lg">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm sm:text-base">
                    <p>
                      <span className="font-medium text-gray-300">Comprobante #:</span> {comprobante.id}
                    </p>
                    <p>
                      <span className="font-medium text-gray-300">Tipo de Pago:</span> {comprobante.tipo_pago}
                    </p>
                    <p>
                      <span className="font-medium text-gray-300">Número de Transacción:</span> {comprobante.numero_transaccion}
                    </p>
                    <p>
                      <span className="font-medium text-gray-300">Fecha de Depósito:</span> {comprobante.fecha_deposito}
                    </p>
                    <p>
                      <span className="font-medium text-gray-300">Monto Total:</span> ${parseFloat(comprobante.monto_total || 0).toFixed(2)}
                    </p>
                    <p>
                      <span className="font-medium text-gray-300">Motivo del Rechazo:</span>{' '}
                      {comprobante.notes || 'No especificado'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
            ref={modalRef}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-notaria-blue-1 bg-opacity-95 p-6 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl sm:text-2xl font-semibold">Justificar Depósito</h3>
                <motion.button
                  onClick={handleCloseModal}
                  className="text-gray-300 hover:text-notaria-yellow transition-colors"
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  aria-label="Cerrar modal"
                >
                  <XMarkIcon className="w-6 h-6" />
                </motion.button>
              </div>
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3">Progreso de Justificación</h4>
                <div className="w-full bg-gray-600 rounded-full h-4 mb-2">
                  <motion.div
                    className="bg-notaria-yellow h-4 rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${justificationProgress}%` }}
                    transition={{ duration: 0.5 }}
                    aria-label={`Progreso de justificación: ${justificationProgress.toFixed(1)}%`}
                  />
                </div>
                <p className="text-sm">Progreso: {justificationProgress.toFixed(1)}%</p>
                <p className="text-sm mt-2">
                  <span className="font-semibold">Trámites Totales Seleccionados:</span> {selectedTramites.length}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Trámites Justificados:</span> {justifiedTramites.length}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Total seleccionado pendiente:</span> ${calculateTotalIngresos}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Efectivo a depositar:</span> ${formatMoneyCents(selectedSplitMetrics.cashAmount)}
                </p>
                <p className="text-sm">
                  <span className="font-semibold">Crédito por cobrar:</span> ${formatMoneyCents(selectedSplitMetrics.creditAmount)}
                </p>
</div>
              <div className="mb-6">
                <h4 className="text-lg font-semibold mb-3">Trámites Seleccionados</h4>
                <div className="max-h-[200px] overflow-y-auto">
                  {selectedTramites.map((tramiteId) => {
                    const tramite = tramiteById.get(tramiteId);
                    if (!tramite) return null;
                    const isJustified = justifiedTramites.includes(tramiteId);
                    return (
                      <div
                        key={tramiteId}
                        className={`flex items-center p-2 mb-2 rounded-lg ${isJustified ? 'bg-green-900 bg-opacity-50' : 'bg-notaria-blue-2'}`}
                      >
                        <input
                          type="checkbox"
                          checked={tramitesToJustify.includes(tramiteId)}
                          onChange={() => handleJustifySelection(tramiteId)}
                          disabled={isJustified}
                          className="h-5 w-5 text-notaria-yellow rounded focus:ring-notaria-yellow mr-2"
                          aria-label={`Seleccionar trámite ${tramite.no_temporal} para justificar`}
                        />
                        <span className="text-sm flex-1">
                          No. Temporal: {tramite.no_temporal} | Total Ajustado: ${formatMoneyCents(getTramiteAmountCents(tramite))} | Tipo:{' '}
                          {tramite.tipo.charAt(0).toUpperCase() + tramite.tipo.slice(1)}
                          {creditAssignments[tramiteId]?.isCredit ? ' | Crédito' : ' | Efectivo'}
                        </span>
                        {isJustified && (
                          <CheckIcon className="w-5 h-5 text-green-500" aria-label="Trámite justificado" />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              <form onSubmit={handleSubmit} encType="multipart/form-data" className="space-y-4">
                {submissionAlert && (
                  <div role="alert" className="rounded-lg border border-red-300 bg-red-950/70 p-4 font-semibold text-red-100">
                    {submissionAlert}
                  </div>
                )}
                <div className="space-y-3 mb-6 text-sm sm:text-base">
                  <p>
                    <span className="font-semibold">Trámites a Justificar Ahora:</span> {tramitesToJustify.length}
                  </p>
                  <p>
                    <span className="font-semibold">Total seleccionado ahora:</span> ${calculateCurrentJustificationIngresos}
                  </p>
                  <p>
                    <span className="font-semibold">Efectivo a depositar ahora:</span> ${formatMoneyCents(currentSplitMetrics.cashAmount)}
                  </p>
                  <p>
                    <span className="font-semibold">Crédito por cobrar ahora:</span> ${formatMoneyCents(currentSplitMetrics.creditAmount)}
                  </p>
                  <p>
                    <span className="font-semibold">Total pendiente del filtro actual:</span> {selectedVsPendingMetrics.pendingCount} trámites (${selectedVsPendingMetrics.pendingAmount})
                  </p>
                  <p>
                    <span className="font-semibold">Faltantes luego de esta selección:</span> {selectedVsPendingMetrics.missingCount} trámites (${selectedVsPendingMetrics.missingAmount})
                  </p>
                  <p>
                    <span className="font-semibold">Total Comprobantes:</span> ${calculateTotalComprobantes}
                  </p>
                  <p>
                    <span className="font-semibold">Diferencia:</span>{' '}
                    ${formatMoneyCents(currentSplitMetrics.cashAmount - calculateTotalComprobantesCents)}
                  </p>
                </div>
                {currentSplitMetrics.cashCount === 0 && (
                  <div className="rounded-lg border border-notaria-yellow/40 bg-notaria-yellow/10 p-4 text-sm text-notaria-yellow">
                    Todos los trámites seleccionados ahora son a crédito. No se requiere comprobante de depósito diario.
                  </div>
                )}
                {receiptBlockingMessages.length > 0 && (
                  <div
                    id="receipt-blocking-summary"
                    role="alert"
                    aria-live="assertive"
                    aria-label="Resumen de bloqueos de comprobantes"
                    className="rounded border border-red-400/50 bg-red-950/50 p-4 text-sm text-red-100"
                  >
                    <p className="font-semibold">No se puede enviar la justificación:</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {receiptBlockingMessages.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                  </div>
                )}
                {receiptReviewMessages.length > 0 && (
                  <div
                    id="receipt-review-summary"
                    role="status"
                    aria-live="polite"
                    aria-label="Avisos de revisión manual"
                    className="rounded border border-notaria-yellow/50 bg-notaria-yellow/10 p-4 text-sm text-notaria-yellow"
                  >
                    <p className="font-semibold">Revisión manual requerida:</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {receiptReviewMessages.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                  </div>
                )}
                {currentSplitMetrics.cashCount > 0 && comprobantes.map((comprobante, index) => (
                  <fieldset
                    key={comprobante.clientId}
                    className="space-y-4 p-4 bg-notaria-blue-2 rounded-lg"
                    aria-label={`Comprobante ${index + 1}`}
                  >
                    <legend className="flex w-full items-center justify-between px-1 text-lg font-semibold">
                      <span>Comprobante {index + 1}</span>
                      {comprobantes.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeComprobante(index)}
                          className="text-sm font-semibold text-red-300 hover:text-red-100 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                          aria-label={`Eliminar comprobante ${index + 1}`}
                        >
                          <XMarkIcon className="mr-1 inline h-5 w-5" aria-hidden="true" />
                          Eliminar
                        </button>
                      )}
                    </legend>
                    <div>
                      <label htmlFor={`bank_kind_${index}`} className="block text-sm font-semibold mb-1">
                        Banco
                      </label>
                      <select
                        id={`bank_kind_${index}`}
                        value={comprobante.bank_kind}
                        onChange={(e) => handleComprobanteChange(index, 'bank_kind', e.target.value)}
                        className="w-full p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                        required
                        aria-invalid={Boolean(receiptErrors[index]?.bank_kind)}
                        aria-describedby={receiptErrors[index]?.bank_kind ? `receipt_${comprobante.clientId}_bank_kind_error` : undefined}
                      >
                        <option value="">Seleccionar banco</option>
                        <option value="pichincha">Banco Pichincha</option>
                        <option value="other">Otro banco</option>
                      </select>
                      {receiptErrors[index]?.bank_kind && (
                        <p id={`receipt_${comprobante.clientId}_bank_kind_error`} className="mt-1 text-sm text-red-200">
                          {receiptErrors[index].bank_kind}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor={`tipo_pago_${index}`} className="block text-sm font-semibold mb-1">
                        Tipo de Pago
                      </label>
                      <select
                        id={`tipo_pago_${index}`}
                        value={comprobante.tipo_pago}
                        onChange={(e) => handleComprobanteChange(index, 'tipo_pago', e.target.value)}
                        className="w-full p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                        required
                        aria-invalid={Boolean(receiptErrors[index]?.tipo_pago)}
                        aria-describedby={receiptErrors[index]?.tipo_pago ? `receipt_${comprobante.clientId}_tipo_pago_error` : undefined}
                      >
                        <option value="">Seleccionar tipo de pago</option>
                        <option value="deposito">Depósito</option>
                        <option value="transferencia">Transferencia</option>
                        <option value="tarjeta_credito">Tarjeta de Crédito</option>
                        <option value="nota_credito">Nota de Crédito</option>
                      </select>
                      {receiptErrors[index]?.tipo_pago && (
                        <p id={`receipt_${comprobante.clientId}_tipo_pago_error`} className="mt-1 text-sm text-red-200">
                          {receiptErrors[index].tipo_pago}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor={`archivo_${index}`} className="block text-sm font-semibold mb-1">
                        Subir Comprobante (PDF, PNG, JPG)
                      </label>
                      <input
                        type="file"
                        id={`archivo_${index}`}
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={(e) => handleFileChange(index, e)}
                        className="w-full p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-notaria-yellow file:text-notaria-blue-1 hover:file:bg-notaria-red hover:file:text-white"
                        required
                        aria-invalid={Boolean(receiptErrors[index]?.archivo)}
                        aria-describedby={[
                          receiptErrors[index]?.archivo ? `receipt_${comprobante.clientId}_archivo_error` : '',
                          comprobante.bank_kind === 'pichincha' && comprobante.preflightStatus !== 'idle'
                            ? `receipt_${comprobante.clientId}_status`
                            : '',
                        ].filter(Boolean).join(' ') || undefined}
                      />
                      {receiptErrors[index]?.archivo && (
                        <p id={`receipt_${comprobante.clientId}_archivo_error`} className="mt-1 text-sm text-red-200">
                          {receiptErrors[index].archivo}
                        </p>
                      )}
                    </div>
                    {comprobante.previewUrl && (
                      <div className="mt-2">
                        <button
                          type="button"
                          onClick={(e) => togglePreview(e, index)}
                          className="text-notaria-yellow hover:underline focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                          aria-label={
                            comprobante.showPreview
                              ? `Ocultar vista previa del comprobante ${index + 1}`
                              : `Ver vista previa del comprobante ${index + 1}`
                          }
                        >
                          {comprobante.showPreview ? 'Ocultar Vista Previa' : 'Ver Vista Previa'}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveFile(index)}
                          className="ml-4 text-red-500 hover:text-red-400 transition-colors"
                          aria-label={`Eliminar archivo del comprobante ${index + 1}`}
                        >
                          <XMarkIcon className="w-5 h-5 inline mr-1" />
                          Eliminar Archivo
                        </button>
                        {comprobante.showPreview && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: 10 }}
                            transition={{ duration: 0.3 }}
                            className="mt-4 bg-white rounded-lg p-4"
                          >
                            <div className="flex justify-between items-center mb-2">
                              <h5 className="text-base font-semibold text-black">Vista Previa del Comprobante</h5>
                              <div className="flex items-center space-x-2">
                                <motion.button
                                  onClick={() => handleZoomOut(index)}
                                  className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  aria-label="Reducir zoom"
                                >
                                  <MagnifyingGlassMinusIcon className="w-5 h-5" />
                                </motion.button>
                                <motion.button
                                  onClick={() => handleZoomIn(index)}
                                  className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                  aria-label="Aumentar zoom"
                                >
                                  <MagnifyingGlassPlusIcon className="w-5 h-5" />
                                </motion.button>
                                <a
                                  href={comprobante.previewUrl}
                                  download
                                  className="text-gray-600 hover:text-notaria-yellow transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                                  aria-label="Descargar comprobante"
                                >
                                  <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                                    <DocumentArrowDownIcon className="w-5 h-5" />
                                  </motion.div>
                                </a>
                              </div>
                            </div>
                            {comprobante.fileError ? (
                              <div className="text-center text-red-600">
                                <p>{comprobante.fileError}</p>
                                <a
                                  href={comprobante.previewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-notaria-yellow hover:underline mt-2 inline-block"
                                  aria-label="Abrir archivo en una nueva pestaña"
                                >
                                  Abrir en una nueva pestaña
                                </a>
                              </div>
                            ) : comprobante.archivo.type === 'application/pdf' ? (
                              <div className="overflow-y-auto max-h-[50vh] flex justify-center">
                                <Document
                                  file={comprobante.previewUrl}
                                  onLoadSuccess={(pdf) => onDocumentLoadSuccess(index, pdf)}
                                  onLoadError={() => onDocumentLoadError(index)}
                                >
                                  <Page pageNumber={1} scale={comprobante.zoomLevel} className="mb-4" />
                                </Document>
                              </div>
                            ) : (
                              <div className="flex justify-center">
                                <img
                                  src={comprobante.previewUrl}
                                  alt={`Vista previa del comprobante ${index + 1}`}
                                  className="max-w-full max-h-[50vh] object-contain"
                                  style={{ transform: `scale(${comprobante.zoomLevel})`, transition: 'transform 0.3s ease' }}
                                  onError={() => onImageLoadError(index)}
                                />
                              </div>
                            )}
                          </motion.div>
                        )}
                      </div>
                    )}
                    {comprobante.bank_kind === 'other' && (
                      <>
                        <div role="status" className="rounded border border-notaria-yellow/50 bg-notaria-yellow/10 p-3 text-sm text-notaria-yellow">
                          Este comprobante requiere revisión manual del notario y nunca se considera preaprobado por QR.
                        </div>
                        <div>
                          <label htmlFor={`bank_name_${index}`} className="block text-sm font-semibold mb-1">
                            Nombre del Banco
                          </label>
                          <input
                            type="text"
                            id={`bank_name_${index}`}
                            value={comprobante.bank_name}
                            onChange={(e) => handleComprobanteChange(index, 'bank_name', e.target.value)}
                            className="w-full p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                            required
                            maxLength={120}
                            aria-invalid={Boolean(receiptErrors[index]?.bank_name)}
                            aria-describedby={receiptErrors[index]?.bank_name ? `receipt_${comprobante.clientId}_bank_name_error` : undefined}
                          />
                          {receiptErrors[index]?.bank_name && (
                            <p id={`receipt_${comprobante.clientId}_bank_name_error`} className="mt-1 text-sm text-red-200">
                              {receiptErrors[index].bank_name}
                            </p>
                          )}
                        </div>
                      </>
                    )}
                    <div>
                      <label htmlFor={`numero_transaccion_${index}`} className="block text-sm font-semibold mb-1">
                        Número de Transacción
                      </label>
                      <input
                        type="text"
                        id={`numero_transaccion_${index}`}
                        value={comprobante.numero_transaccion}
                        onChange={(e) => handleComprobanteChange(index, 'numero_transaccion', e.target.value)}
                        className="w-full p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                        required
                        readOnly={comprobante.bank_kind === 'pichincha' && !isManualQrReview(comprobante)}
                        aria-invalid={Boolean(receiptErrors[index]?.numero_transaccion)}
                        aria-describedby={[
                          receiptErrors[index]?.numero_transaccion ? `receipt_${comprobante.clientId}_numero_transaccion_error` : '',
                          comprobante.bank_kind === 'pichincha' && comprobante.preflightStatus !== 'idle'
                            ? `receipt_${comprobante.clientId}_status`
                            : '',
                        ].filter(Boolean).join(' ') || undefined}
                      />
                      {receiptErrors[index]?.numero_transaccion && (
                        <p id={`receipt_${comprobante.clientId}_numero_transaccion_error`} className="mt-1 text-sm text-red-200">
                          {receiptErrors[index].numero_transaccion}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor={`fecha_deposito_${index}`} className="block text-sm font-semibold mb-1">
                        Fecha de Depósito
                      </label>
                      <input
                        type="date"
                        id={`fecha_deposito_${index}`}
                        value={comprobante.fecha_deposito}
                        onChange={(e) => handleComprobanteChange(index, 'fecha_deposito', e.target.value)}
                        className="w-full p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                        required
                        readOnly={comprobante.bank_kind === 'pichincha' && !isManualQrReview(comprobante)}
                        aria-invalid={Boolean(receiptErrors[index]?.fecha_deposito)}
                        aria-describedby={[
                          receiptErrors[index]?.fecha_deposito ? `receipt_${comprobante.clientId}_fecha_deposito_error` : '',
                          comprobante.bank_kind === 'pichincha' && comprobante.preflightStatus !== 'idle'
                            ? `receipt_${comprobante.clientId}_status`
                            : '',
                        ].filter(Boolean).join(' ') || undefined}
                      />
                      {receiptErrors[index]?.fecha_deposito && (
                        <p id={`receipt_${comprobante.clientId}_fecha_deposito_error`} className="mt-1 text-sm text-red-200">
                          {receiptErrors[index].fecha_deposito}
                        </p>
                      )}
                    </div>
                    <div>
                      <label htmlFor={`monto_total_${index}`} className="block text-sm font-semibold mb-1">
                        Monto del Comprobante
                      </label>
                      <input
                        type="number"
                        id={`monto_total_${index}`}
                        value={comprobante.monto_total}
                        onChange={(e) => handleComprobanteChange(index, 'monto_total', e.target.value)}
                        className="w-full p-2 rounded-lg bg-notaria-blue-3 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                        required
                        min="0.01"
                        step="0.01"
                        readOnly={comprobante.bank_kind === 'pichincha' && !isManualQrReview(comprobante)}
                        aria-invalid={Boolean(receiptErrors[index]?.monto_total)}
                        aria-describedby={[
                          receiptErrors[index]?.monto_total ? `receipt_${comprobante.clientId}_monto_total_error` : '',
                          comprobante.bank_kind === 'pichincha' && comprobante.preflightStatus !== 'idle'
                            ? `receipt_${comprobante.clientId}_status`
                            : '',
                        ].filter(Boolean).join(' ') || undefined}
                      />
                      {receiptErrors[index]?.monto_total && (
                        <p id={`receipt_${comprobante.clientId}_monto_total_error`} className="mt-1 text-sm text-red-200">
                          {receiptErrors[index].monto_total}
                        </p>
                      )}
                    </div>
                    {comprobante.bank_kind === 'pichincha' && (
                      <div>
                        <label className="flex items-center gap-2 text-sm font-semibold">
                          <input
                            type="checkbox"
                            checked={comprobante.qr_manual_review}
                            disabled={comprobante.preflightCode !== 'qr_unreadable' || comprobante.preflightStatus !== 'error'}
                            onChange={(e) => handleComprobanteChange(index, 'qr_manual_review', e.target.checked)}
                          />
                          QR ilegible: solicitar revisión manual
                        </label>
                        {isManualQrReview(comprobante) && (
                          <div className="mt-3 space-y-3">
                            <label className="block text-sm font-semibold">
                              Titular declarado de la cuenta
                              <input
                                type="text" required maxLength={150}
                                value={comprobante.manual_account_owner}
                                onChange={(e) => handleComprobanteChange(index, 'manual_account_owner', e.target.value)}
                                className="mt-1 w-full rounded bg-notaria-blue-3 p-2 text-white"
                                aria-invalid={Boolean(receiptErrors[index]?.manual_account_owner)}
                              />
                            </label>
                            <label className="block text-sm font-semibold">
                              Motivo de revisión manual
                              <input
                                type="text" required maxLength={2000}
                                value={comprobante.qr_override_reason}
                                onChange={(e) => handleComprobanteChange(index, 'qr_override_reason', e.target.value)}
                                className="mt-1 w-full rounded bg-notaria-blue-3 p-2 text-white"
                                aria-invalid={Boolean(receiptErrors[index]?.qr_override_reason)}
                              />
                            </label>
                          </div>
                        )}
                      </div>
                    )}
                    {comprobante.bank_kind === 'pichincha' && comprobante.preflightStatus === 'loading' && (
                      <div id={`receipt_${comprobante.clientId}_status`} role="status" aria-live="polite" className="flex items-center gap-2 rounded border border-white/20 p-3 text-sm text-gray-100">
                        <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />
                        Verificando QR de Banco Pichincha
                      </div>
                    )}
                    {comprobante.bank_kind === 'pichincha' && comprobante.preflightStatus === 'verified' && (
                      <div id={`receipt_${comprobante.clientId}_status`} role="status" aria-live="polite" className="rounded border border-green-400/50 bg-green-950/40 p-3 text-sm text-green-100">
                        <p className="font-semibold">QR verificado</p>
                        <p>Campos del QR para inspección humana; esta lectura no certifica autenticidad bancaria.</p>
                        <p><span className="font-semibold">Titular de la cuenta QR:</span>{' '}{comprobante.preflightResult?.account_owner}</p>
                      </div>
                    )}
                    {comprobante.bank_kind === 'pichincha' && comprobante.preflightStatus === 'error' && (
                      <div
                        id={`receipt_${comprobante.clientId}_status`}
                        role="alert"
                        aria-live="assertive"
                        aria-label={`${isManualQrReview(comprobante) ? 'Revisión manual' : 'Error'} de comprobante ${index + 1}`}
                        className={isManualQrReview(comprobante)
                          ? 'rounded border border-amber-400/50 bg-amber-950/50 p-3 text-sm text-amber-100'
                          : 'rounded border border-red-400/50 bg-red-950/50 p-3 text-sm text-red-100'}
                      >
                        {isManualQrReview(comprobante)
                          ? 'QR no verificado: pendiente de revisión manual del notario. Datos declarados, sin preaprobación automática.'
                          : comprobante.preflightError}
                      </div>
                    )}
                    {comprobante.bank_kind === 'pichincha' && comprobante.preflightStatus === 'transient-error' && (
                      <div
                        id={`receipt_${comprobante.clientId}_status`}
                        role="alert"
                        aria-live="assertive"
                        aria-label={`Error de comprobante ${index + 1}`}
                        className="rounded border border-red-400/50 bg-red-950/50 p-3 text-sm text-red-100"
                      >
                        <p>{comprobante.preflightError}</p>
                        <button
                          type="button"
                          onClick={() => retryPreflight(comprobante.clientId)}
                          className="mt-2 font-semibold text-notaria-yellow underline focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                          aria-label={`Reintentar verificación del comprobante ${index + 1}`}
                        >
                          Reintentar
                        </button>
                      </div>
                    )}
                  </fieldset>
                ))}
                {currentSplitMetrics.cashCount > 0 && comprobantes.length < MAX_RECEIPTS && (
                  <motion.button
                    type="button"
                    onClick={addComprobante}
                    className="w-full bg-notaria-blue-2 text-white font-semibold py-2 rounded-lg mb-3 hover:bg-notaria-blue-3 transition-all"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    aria-label="Agregar otro comprobante"
                  >
                    <PlusCircleIcon className="w-5 h-5 inline mr-2" />
                    Agregar Otro Comprobante
                  </motion.button>
                )}
                <motion.button
                  ref={submitButtonRef}
                  type="submit"
                  disabled={isSubmitDisabled}
                  className={`w-full bg-notaria-yellow text-notaria-blue-1 font-bold py-2 rounded-lg hover:bg-notaria-red hover:text-white transition-all ${
                    isSubmitDisabled ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                  whileHover={{ scale: !isSubmitDisabled ? 1.02 : 1 }}
                  whileTap={{ scale: !isSubmitDisabled ? 0.98 : 1 }}
                  aria-label="Justificar trámites seleccionados"
                  aria-describedby={receiptBlockingMessages.length > 0 ? 'receipt-blocking-summary' : undefined}
                >
                  {isLoadingSubmit ? (
                    <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
                  ) : (
                    <CheckCircleIcon className="w-5 h-5 inline mr-2" />
                  )}
                  Justificar Seleccionados
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default JustifyDeposits;
